import React, { useState, useEffect, useRef } from 'react';
import AuthService from '../services/AuthService';
import './HelperWindows.css';

function AICoaching() {
  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [coachingMessages, setCoachingMessages] = useState([]);
  const [latestHint, setLatestHint] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [trainingMode, setTrainingMode] = useState('training');
  const wsRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Check authentication on component mount
  useEffect(() => {
    if (!AuthService.isAuthenticated()) {
      // Redirect to main app for authentication
      window.location.href = '/';
      return;
    }
    
    setIsAuthenticated(true);
    setLoading(false);
    
    // Listen for logout events from other windows
    const handleLogout = () => {
      window.location.href = '/';
    };
    
    window.addEventListener('auth-logout', handleLogout);
    return () => window.removeEventListener('auth-logout', handleLogout);
  }, []);

  // WebSocket connection management
  const connectToCoaching = (sessionId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Get JWT token for authentication
    const token = AuthService.getIdToken();
    if (!token) {
      console.error('No JWT token available for coaching service');
      setWsStatus('error');
      return;
    }

    // Use external domain for WebSocket connection through ALB with JWT token
    const wsUrl = `wss://agent-mentor.penmen.net/coaching-feed/${sessionId}?token=${encodeURIComponent(token)}`;
    console.log('Connecting to coaching service:', wsUrl.replace(/token=[^&]+/, 'token=***'));
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('Connected to coaching service');
        setWsStatus('connected');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('Received coaching message:', message);
          
          if (message.type === 'transcript_update') {
            // Store message with inline coaching hint
            setCoachingMessages(prev => [...prev, {
              timestamp: new Date().toLocaleTimeString(),
              speaker: message.data.speaker,
              text: message.data.text,
              type: 'transcript',
              coaching_hint: message.data.coaching_hint || null  // Store hint with message
            }]);
            
            // Also update latest hint for top panel
            if (message.data.coaching_hint) {
              setLatestHint({
                hint: message.data.coaching_hint.hint,
                stage: message.data.coaching_hint.stage,
                reasoning: message.data.coaching_hint.reasoning,
                timestamp: new Date().toLocaleTimeString()
              });
              console.log('Coaching hint received:', message.data.coaching_hint);
            }
          }
        } catch (e) {
          console.error('Failed to parse coaching message:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('Disconnected from coaching service');
        setWsStatus('disconnected');
      };
      
      wsRef.current.onerror = (error) => {
        console.error('Coaching WebSocket error:', error);
        setWsStatus('error');
      };
    } catch (error) {
      console.error('Failed to connect to coaching service:', error);
      setWsStatus('error');
    }
  };

  // Generate session ID and connect on component mount
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Get user ID from JWT token
    const user = AuthService.getCurrentUser();
    if (!user || !user.userId) {
      console.error('No user ID available for session generation');
      return;
    }
    
    // Get persona ID and training mode from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const personaId = urlParams.get('persona') || 'default';
    const mode = urlParams.get('mode') || 'training';
    setTrainingMode(mode);
    
    // Create deterministic session ID format: session-{user_id}-{persona_id}
    const sessionId = `session-${user.userId}-${personaId}`;
    sessionIdRef.current = sessionId;
    
    console.log('Generated session ID:', sessionId);
    console.log('Training mode:', mode);
    connectToCoaching(sessionIdRef.current);
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [isAuthenticated]);

  useEffect(() => {
    fetch('/data/call_scripts.json')
      .then(res => res.json())
      .then(data => {
        setScripts(data.call_scripts);
        setSelectedScript(data.call_scripts[0]);
      })
      .catch(err => console.error('Failed to load call scripts:', err));
  }, []);

  if (loading) {
    return (
      <div className="helper-window">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          fontFamily: 'Arial, sans-serif'
        }}>
          Checking authentication...
        </div>
      </div>
    );
  }

  return (
    <div className="helper-window">
      <h1>AI Coaching</h1>
      
      {/* Training Mode Indicator */}
      <div className="mode-indicator">
        <span className={`mode-badge ${trainingMode}`}>
          {trainingMode === 'training' ? '🎓 Training Mode' : '📊 Evaluation Mode'}
        </span>
        {trainingMode === 'evaluation' && (
          <span className="mode-note">Coaching hints disabled - session will be evaluated after completion</span>
        )}
      </div>
      
      {/* Coaching Status */}
      <div className="coaching-status">
        <span className={`status-indicator ${wsStatus}`}></span>
        Real-time Coaching: {wsStatus === 'connected' ? 'Active' : 'Inactive'}
        {sessionIdRef.current && <span className="session-id">Session: {sessionIdRef.current}</span>}
      </div>

      {/* AI Coaching Hint */}
      {latestHint && (
        <div className="coaching-hint-panel">
          <div className="hint-header">
            <span className="hint-icon">💡</span>
            <span className="hint-title">AI Coaching Suggestion</span>
            <span className="hint-stage">{latestHint.stage}</span>
          </div>
          <div className="hint-content">
            <div className="hint-text">{latestHint.hint}</div>
            <details className="hint-reasoning">
              <summary>Why this matters</summary>
              <p>{latestHint.reasoning}</p>
            </details>
          </div>
          <div className="hint-timestamp">{latestHint.timestamp}</div>
        </div>
      )}

      {/* Real-time Coaching Panel */}
      <div className="coaching-panel">
        <div className="panel-header" onClick={() => setIsCollapsed(!isCollapsed)}>
          <h3>Live Coaching Feed</h3>
          <button className="collapse-toggle" aria-label={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? '▼' : '▲'}
          </button>
        </div>
        <div className={`coaching-messages ${isCollapsed ? 'collapsed' : ''}`}>
          {coachingMessages.length === 0 ? (
            <div className="no-messages">Waiting for conversation to start...</div>
          ) : (
            (isCollapsed ? coachingMessages.slice(-3) : coachingMessages).map((msg, index) => (
              <div key={index} className="message-with-hint">
                <div className={`coaching-message ${msg.speaker}`}>
                  <span className="timestamp">{msg.timestamp}</span>
                  <span className="speaker">{msg.speaker}:</span>
                  <span className="text">{msg.text}</span>
                </div>
                {msg.coaching_hint && (
                  <div className="inline-hint">
                    <div className="inline-hint-header">
                      <span className="inline-hint-icon">💡</span>
                      <span className="inline-hint-stage">{msg.coaching_hint.stage}</span>
                    </div>
                    <div className="inline-hint-text">{msg.coaching_hint.hint}</div>
                    <details className="inline-hint-reasoning">
                      <summary>Why this matters</summary>
                      <p>{msg.coaching_hint.reasoning}</p>
                    </details>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
      
      <div className="persona-selector">
        <select 
          value={selectedScript?.persona_id || ''} 
          onChange={(e) => setSelectedScript(scripts.find(s => s.persona_id === e.target.value))}
        >
          {scripts.map(script => (
            <option key={script.persona_id} value={script.persona_id}>
              {script.persona_name}
            </option>
          ))}
        </select>
      </div>

      {selectedScript && (
        <div className="script-content">
          <div className="product-rec">
            <strong>Recommended Product:</strong> {selectedScript.recommended_product}
          </div>

          <div className="script-section">
            <h3>Opening</h3>
            <p className="script-text">{selectedScript.script.opening.text}</p>
            <div className="tone">Tone: {selectedScript.script.opening.tone}</div>
          </div>

          {selectedScript.script.basic_insurance_education && (
            <div className="script-section">
              <h3>Basic Insurance Education</h3>
              {Object.entries(selectedScript.script.basic_insurance_education).map(([key, value]) => (
                <div key={key} className="education-item">
                  <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>
                  <p>{value}</p>
                </div>
              ))}
            </div>
          )}

          <div className="script-section">
            <h3>Discovery Questions</h3>
            <ul>
              {selectedScript.script.discovery_questions.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ul>
          </div>

          <div className="script-section">
            <h3>Product Presentation</h3>
            <p className="script-text">{selectedScript.script.product_presentation.text}</p>
            <ul>
              {selectedScript.script.product_presentation.key_benefits.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>

          <div className="script-section">
            <h3>Objection Handling</h3>
            {Object.entries(selectedScript.script.objection_handling).map(([key, value]) => (
              <div key={key} className="objection">
                <strong>{key.replace('_', ' ').toUpperCase()}:</strong>
                <p>{value}</p>
              </div>
            ))}
          </div>

          <div className="script-section">
            <h3>Closing</h3>
            <p className="script-text">{selectedScript.script.closing.text}</p>
            {selectedScript.script.closing.alternative_close && (
              <p className="alternative"><strong>Alternative:</strong> {selectedScript.script.closing.alternative_close}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AICoaching;
